import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Sparkles, Check } from 'lucide-react';
import { API, withCreds, smallInputCls, btnPrimary, errMsg } from './AdminUI';

let statusCache = null;

export function useAIStatus() {
  const [status, setStatus] = useState(statusCache);
  useEffect(() => {
    if (statusCache) return;
    axios.get(`${API}/admin/ai/status`, withCreds)
      .then(r => { statusCache = r.data; setStatus(r.data); })
      .catch(() => setStatus({ copy: false, lifestyle_mockups: false, design_mockups: false }));
  }, []);
  return status;
}

const FIELDS = [
  ['title', 'Title'], ['description', 'Description'], ['tags', 'Tags'], ['seo', 'SEO title & description'],
];

export default function AIAssist({ form, productId, onApply }) {
  const status = useAIStatus();
  const [open, setOpen] = useState(false);
  const [want, setWant] = useState({ title: true, description: true, tags: true, seo: true });
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  if (!status) return null;
  if (!status.copy) {
    return <p className="text-ink/45 text-xs">AI writing is off. Add <code className="text-honey-700">ANTHROPIC_API_KEY</code> to the backend environment to turn it on.</p>;
  }

  const generate = async () => {
    setBusy(true); setError(''); setResult(null);
    try {
      const { title, description, category, tags, images, price, options, personalizations, weight_oz } = form;
      const r = await axios.post(`${API}/admin/ai/product-copy`, {
        product_id: productId || undefined,
        product: { title, description, category, tags, images, price, options, personalizations, weight_oz },
        instructions: notes,
      }, withCreds);
      setResult(r.data.suggestion);
    } catch (e) { setError(errMsg(e, 'AI request failed')); }
    finally { setBusy(false); }
  };

  const fieldsFor = (key) => (key === 'seo' ? ['seo_title', 'seo_description'] : [key]);
  const apply = (keys) => {
    const patch = {};
    keys.flatMap(fieldsFor).forEach(f => { patch[f] = result[f]; });
    onApply(patch);
  };
  const chosen = FIELDS.map(([k]) => k).filter(k => want[k]);

  return (
    <div className="rounded-xl border border-honey/20 bg-honey/5 p-4">
      <button type="button" onClick={() => setOpen(o => !o)} className="flex items-center gap-2 text-honey-700 text-sm font-bold">
        <Sparkles className="w-4 h-4" /> Write with AI
      </button>
      {open && (
        <div className="mt-3 space-y-3">
          <p className="text-ink/55 text-xs">Uses the photos and any details already filled in. Nothing changes until you click Use.</p>
          <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional notes: tone, keywords, details to mention (e.g. handmade in Ohio, dishwasher safe)"
            className={`${smallInputCls} w-full`} />
          <button type="button" onClick={generate} disabled={busy} className={btnPrimary}>
            <Sparkles className="w-4 h-4" /> {busy ? 'Writing… (about 10–30 seconds)' : result ? 'Try again' : 'Generate suggestions'}
          </button>
          {error && <p className="text-red-600 text-sm">{error}</p>}
          {result && (
            <div className="space-y-3">
              {FIELDS.map(([key, label]) => (
                <div key={key} className="p-3 rounded-lg bg-black/30 border border-ink/10">
                  <div className="flex items-center justify-between mb-1">
                    <label className="flex items-center gap-2 text-ink/70 text-xs uppercase tracking-widest cursor-pointer">
                      <input type="checkbox" checked={want[key]} onChange={e => setWant({ ...want, [key]: e.target.checked })} className="accent-honey" /> {label}
                    </label>
                    <button type="button" onClick={() => apply([key])} className="text-honey-700 text-xs font-bold hover:underline">Use</button>
                  </div>
                  {key === 'tags' ? (
                    <div className="flex flex-wrap gap-1">{result.tags.map(t => <span key={t} className="text-xs px-2 py-0.5 rounded-full bg-ink/[0.07] text-ink/75">{t}</span>)}</div>
                  ) : key === 'seo' ? (
                    <div className="text-sm">
                      <p className="text-[#1a0dab]">{result.seo_title} <span className="text-ink/45 text-xs">({result.seo_title.length})</span></p>
                      <p className="text-ink/70">{result.seo_description} <span className="text-ink/45 text-xs">({result.seo_description.length})</span></p>
                    </div>
                  ) : (
                    <p className="text-ink/85 text-sm whitespace-pre-line">{result[key]}</p>
                  )}
                </div>
              ))}
              <button type="button" onClick={() => apply(chosen)} disabled={!chosen.length} className={btnPrimary}>
                <Check className="w-4 h-4" /> Use checked ({chosen.length})
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
